import React, { useMemo, useState } from 'react';
import { useLazyQuery, useQuery } from '@common/hooks';
import {
  InvoiceFiltersQuery,
  InvoiceFiltersQueryResponse,
  InvoiceListItemFragmentType,
  PaginatedInvoicesQuery,
  PaginatedInvoicesQueryArgs,
  PaginatedInvoicesQueryResponse,
} from '@pay-bill/graphql';
import {
  Currency,
  DateRangeFilter,
  ErrorHandler,
  Link,
  RenderFilteredTable,
  RenderTableColumn,
  RenderTableOnChange,
  Text,
  TextFilterSearch,
  TimeWithTimeZone,
} from '@common/components';
import { Spacing } from 'works/legacy/lib/style-helper';
import {
  INVOICE_LABEL_OVERWRITE,
  InvoiceActionsButton,
  InvoiceBulkActionFooter,
  InvoiceState,
  TotalCountLabel,
} from '@pay-bill/components';
import { filter } from 'lodash';
import {
  localStorageStore,
  useStoredFilters,
} from 'works/legacy/lib/hooks/use-stored-filters';
import { useFilterCallbacks } from 'works/legacy/lib/hooks/use-filter-callbacks';
import { toUSCompactDateFormat } from 'works/legacy/lib/date-helper';
import { CLEANUP_WHEN_EMPTY, nonFalse } from '@pay-bill/services';

type OrderField = PaginatedInvoicesQueryArgs['orderField'];

const defaultFilters: PaginatedInvoicesQueryArgs['filters'] = Object.freeze({
  states: ['open', 'paid'],
});

const defaultOrder: OrderField = 'CREATED_AT';

export const BillingInvoiceList = () => {
  const [filters, setFilters] = useStoredFilters('InvoiceTable', {
    store: localStorageStore,
    defaults: defaultFilters,
  });
  const { onClearFilter, onClearAllFilters } = useFilterCallbacks(setFilters);

  const [pageData, setPageData] = useState(() => ({
    limit: 50,
    offset: 0,
    orderDirection: 'DESC' as PaginatedInvoicesQueryArgs['orderDirection'],
    orderField: defaultOrder as OrderField,
  }));

  const [selectedInvoices, setSelectedInvoices] = useState<
    InvoiceListItemFragmentType[]
  >([]);

  const [
    fetchFilterData,
    { data: filterOptionsData, error: filterOptionsError },
  ] = useLazyQuery<InvoiceFiltersQueryResponse>(InvoiceFiltersQuery);

  const { data, error, loading } = useQuery<
    PaginatedInvoicesQueryResponse,
    PaginatedInvoicesQueryArgs
  >(PaginatedInvoicesQuery, {
    notifyOnNetworkStatusChange: true,
    variables: {
      ...pageData,
      filters: filters,
    },
    onCompleted: () => fetchFilterData(),
  });

  const { statesFilter, suppliersFilter } = useMemo(() => {
    const options = filterOptionsData?.worksInvoiceFilters;

    return {
      statesFilter: [
        ...new Set([
          ...(options?.states || []),
          ...(defaultFilters.states || []),
        ]),
      ],
      suppliersFilter: options?.suppliers || [],
    };
  }, [filterOptionsData?.worksInvoiceFilters]);

  const invoicesColumns = filter<
    RenderTableColumn<InvoiceListItemFragmentType> | false,
    RenderTableColumn<InvoiceListItemFragmentType>
  >(
    [
      {
        title: 'Invoice #',
        key: 'code',
        dataIndex: 'code',
        className: 'invoice-code-col',
        width: 200,
        sorter: true,
        filterFormat: 'id',
        filtered: !!filters.code?.length,
        filteredValue: filters.code || CLEANUP_WHEN_EMPTY,
        filterDropdown: ({ setSelectedKeys, confirm }) => (
          <section style={{ padding: `${Spacing.baseSpacing / 2}px` }}>
            <TextFilterSearch
              autoFocus
              id="filter-by-invoice-code-input"
              placeholder="Filter by code"
              onSearch={(value) => {
                setSelectedKeys(value ? [value] : []);
                confirm();
              }}
            />
          </section>
        ),
        render: (text, record) => (
          <Text color="elm" weight="bold">
            <Link to={`/works/billing/invoices/${record.id}/summary`}>
              {text}
            </Link>
          </Text>
        ),
      },
      {
        title: 'Supplier',
        key: 'suppliers',
        dataIndex: 'worksReverseInvoices',
        className: 'suppliers-col',
        sorter: true,
        filtered: !!filters.suppliers?.length,
        filteredValue: filters.suppliers || CLEANUP_WHEN_EMPTY,
        filters: suppliersFilter.map((supplier) => ({
          text: supplier.name,
          value: supplier.id,
        })),
        render: (reverseInvoices) => {
          const names = reverseInvoices
            .map((rinv) => rinv.supplier.name)
            .sort();

          return <div>{names.join(', ')}</div>;
        },
      },
      {
        title: 'Date',
        key: 'date',
        dataIndex: 'createdAt',
        className: 'date-col',
        sorter: true,
        filterFormat: 'dateRange',
        filtered: !!filters.date?.length,
        filteredValue: filters.date || CLEANUP_WHEN_EMPTY,
        render: (date) => <div>{toUSCompactDateFormat(date)}</div>,
        filterDropdown: ({ setSelectedKeys, confirm }) => (
          <section style={{ padding: `${Spacing.baseSpacing / 2}px` }}>
            <DateRangeFilter
              autoFocus
              onSearch={(value) => {
                setSelectedKeys((value || []) as string[]);
                confirm();
              }}
            />
          </section>
        ),
      },
      {
        title: 'Amount',
        dataIndex: 'totalCents',
        className: 'amount-col',
        key: 'amount',
        sorter: true,
        render: (value) => {
          if (!value) {
            return (
              <Text color="sunDarkest" weight="medium">
                Pending
              </Text>
            );
          }

          return <Currency value={value / 100} />;
        },
      },
      {
        title: 'Status',
        dataIndex: 'state',
        className: 'status-col',
        key: 'states',
        sorter: true,
        filtered: !!filters.states?.length,
        filteredValue: filters.states || CLEANUP_WHEN_EMPTY,
        filters: statesFilter.map((filter) => ({
          text: INVOICE_LABEL_OVERWRITE[filter] || filter,
          value: filter,
        })),
        render: (text, record) => (
          <>
            <InvoiceState state={text} />
            {record.stateChangedAt ? (
              <div>
                <Text color="midnightLighter" size="sm">
                  on{' '}
                  <TimeWithTimeZone
                    date={record.stateChangedAt}
                    variant="short_format"
                  />
                </Text>
              </div>
            ) : null}
          </>
        ),
      },
      {
        title: 'Open Disputes',
        dataIndex: 'openedDisputeCount',
        render: (count) => <div>{count} Open</div>,
      },
      {
        title: ' ',
        key: 'actions',
        className: 'actions-col',
        render: (_: unknown, value: InvoiceListItemFragmentType) => (
          <InvoiceActionsButton
            invoice={value}
            styleType="default"
            onFinish={onFinish}
          />
        ),
      },
    ],
    nonFalse
  );

  const page = data?.paginatedWorksInvoices;
  const invoices = page?.worksInvoices || [];
  const totalInvoices = page?.totalCount ?? 0;

  const tablePageInfo = useMemo(
    () => ({
      ...pageData,
      showSizeChanger: true,
      defaultPageSize: pageData.limit,
      total: totalInvoices,
    }),
    [pageData, totalInvoices]
  );

  const onFinish = () => setSelectedInvoices([]);

  const handleTableChange: RenderTableOnChange<InvoiceListItemFragmentType> = (
    pagination,
    filters,
    sorter
  ) => {
    if (sorter instanceof Array) {
      return;
    }

    const pageSize = pagination.pageSize || tablePageInfo.defaultPageSize;
    const orderDirection = sorter.order === 'ascend' ? 'ASC' : 'DESC';
    const orderMap: Record<string, OrderField> = {
      code: 'ID',
      payPeriodStartDate: 'PAY_PERIOD',
      createdAt: 'CREATED_AT',
      amount: 'BILLING_AMOUNT',
      states: 'STATE',
      suppliers: 'SUPPLIERS',
    };
    const orderField =
      orderMap[sorter.columnKey as string | number] || defaultOrder;

    if (filters.length) {
      setSelectedInvoices([]);
    }

    setFilters((oldFilters) => ({
      ...oldFilters,
      ...filters,
    }));
    setPageData(() => ({
      orderField,
      orderDirection,
      offset: ((pagination?.current || 1) - 1) * pageSize,
      limit: pageSize,
    }));
  };

  const rowSelection = {
    selectedRowKeys: selectedInvoices.map((invoice) => invoice.id),
    onChange: (_: unknown, records: unknown) => {
      setSelectedInvoices(records as InvoiceListItemFragmentType[]);
    },
  };

  const anyError = filterOptionsError || error;
  if (anyError) {
    return <ErrorHandler error={anyError} log="BillingInvoiceList" />;
  }

  const selection = Object.values(selectedInvoices);

  return (
    <div className="table-wrapper">
      <TotalCountLabel
        dataLength={invoices.length}
        totalCount={totalInvoices}
        label="line items"
      />
      <RenderFilteredTable
        rowKey="id"
        className="invoices"
        dataSource={invoices}
        columns={invoicesColumns}
        loading={loading}
        onChange={handleTableChange}
        pagination={tablePageInfo}
        filters={filters}
        onClearFilter={onClearFilter}
        onClearAllFilters={onClearAllFilters}
        filtersDataReady={!!filterOptionsData}
        rowSelection={rowSelection}
      />
      {!!selection.length && (
        <InvoiceBulkActionFooter invoices={selection} onFinish={onFinish} />
      )}
      <style jsx>{`
        :global(.invoices .ant-table-row) {
          height: 49px;
        }
      `}</style>
    </div>
  );
};
